"use strict";
// The display category of a bank account, and its label (#719).
//
// Plaid splits the answer across two fields: `account.type` says `depository`
// for both a current account and a savings account, and `account.subtype` says
// which. No single field answers "what kind of account is this", so every
// surface that wanted a label or an icon grew its own ladder, and the ladders
// disagreed. Measured on the dev host on 2026-09-05 (418 accounts, a copy of
// production): the web dashboard adapter called all 63 `investment` accounts
// (401k and IRA) **checking**, and the Banks page called 125 `cash management`
// /`hsa`/`cd`/`money market` accounts **Account**.
//
// The backend now derives this once and serialises it as `account_category`
// (`banking.services.account_category`). This module is the TypeScript mirror,
// for mock fixtures and for a payload from a backend older than that field. The
// parametrised table in `backend/tests/banking/test_account_category.py` is the
// shared contract: change one side and change the other.
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveAccountCategory = resolveAccountCategory;
exports.accountCategoryOf = accountCategoryOf;
exports.formatAccountCategory = formatAccountCategory;
/** Plaid's `account.type` values that mean an investment product.
 *  `brokerage` is the legacy name, still returned by older items. */
const INVESTMENT_ACCOUNT_TYPES = new Set(["investment", "brokerage"]);
/** Depository subtypes a person would call a savings account. This picks a word
 *  and an icon; it is NOT the spendable definition, which is the backend's
 *  TRANSACTIONAL_SUBTYPES and is never re-derived here. */
const SAVINGS_LIKE_SUBTYPES = new Set([
    "savings",
    "cd",
    "money market",
    "money_market",
    "hsa",
]);
/**
 * The display category of an account, from Plaid's `(type, subtype)` pair.
 *
 * An unrecognised **depository** subtype resolves to `checking`: Plaid's
 * depository default is a transaction account, and the alternative is a
 * familiar current account suddenly labelled "Account". An unrecognised
 * **type** resolves to `other`, because there is nothing honest to guess.
 */
function resolveAccountCategory(accountType, accountSubtype) {
    const type = (accountType ?? "").trim().toLowerCase();
    const subtype = (accountSubtype ?? "").trim().toLowerCase();
    if (type === "credit")
        return "credit";
    if (type === "loan")
        return "loan";
    if (INVESTMENT_ACCOUNT_TYPES.has(type))
        return "investment";
    if (type === "depository") {
        return SAVINGS_LIKE_SUBTYPES.has(subtype) ? "savings" : "checking";
    }
    return "other";
}
const ACCOUNT_CATEGORY_VALUES = new Set([
    "checking",
    "savings",
    "credit",
    "loan",
    "investment",
    "other",
]);
/**
 * The category to render for an account: the server's `account_category` when
 * the payload carries one, otherwise the same derivation run locally.
 *
 * Takes the three values rather than the account object, because web reads a
 * snake_case payload and mobile a camelCased one, and one function that works
 * on both is the point of this module.
 *
 * The fallback is not defensive padding. Mock fixtures have no server field at
 * all, and a client can outlive the deploy that added it — and the failure it
 * would otherwise produce is silent (every account rendering as "Account"),
 * which is exactly the class of bug #719 is.
 */
function accountCategoryOf(servedCategory, accountType, accountSubtype) {
    const served = (servedCategory ?? "").trim().toLowerCase();
    if (ACCOUNT_CATEGORY_VALUES.has(served))
        return served;
    return resolveAccountCategory(accountType, accountSubtype);
}
const ACCOUNT_CATEGORY_LABELS = {
    checking: "Checking",
    savings: "Savings",
    credit: "Credit Card",
    loan: "Loan",
    investment: "Investment",
    other: "Account",
};
/** The label shown beside an account. Mirrors `AccountCategory.labels` on the
 *  backend enum, so the API's own choice labels and the UI agree. */
function formatAccountCategory(category) {
    return ACCOUNT_CATEGORY_LABELS[category];
}
//# sourceMappingURL=accountType.js.map